import { getIsElementDescendantOfElement } from './DOMHelper';
import { isNullOrUndefined } from './collection';

/**
 * @returns {string} The selector to be used to find all focusables
 */
export const getFocusableSelector = (): string => {
  return 'ads-search:not([data-disabled]), ads-button:not([data-disabled]), ads-cta-button:not([data-disabled]), ads-dropdown:not([data-disabled]), ads-vertical-tabs:not([data-disabled]), [href],button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
};

/**
 * Returns all the focusable elements under the given container
 * @param {Element | ShadowRoot} container The container element
 * @returns {Element[]} Focusable elements
 */
export const getFocusableElements = (container: Element | ShadowRoot): Element[] => {
  // get all tabbable elements in the container
  return Array.from(container.querySelectorAll(getFocusableSelector()));
};

/**
 * Returns the first focusable element in the container element
 * @param {Element | ShadowRoot} container The container element
 * @returns {Element} First focusable element
 */
export const getFirstFocusableElement = (container: Element | ShadowRoot): Element => {
  const focusable = getFocusableElements(container);
  return focusable.length > 0 ? focusable[0] : null;
};

/**
 * Returns the last focusable element in the container element
 * @param {Element | ShadowRoot} container The container element
 * @returns {Element} Last focusable element
 */
export const getLastFocusableElement = (container: Element | ShadowRoot): Element => {
  const focusable = getFocusableElements(container);
  return focusable.length > 0 ? focusable[focusable.length - 1] : null;
};

/**
 * Sets focus to the first focusable element in a shadowroot
 * @param {ShadowRoot} shadowRoot Shadowroot
 */
const focusElementInShadowRoot = (shadowRoot: ShadowRoot): void => {
  const firstFocusableInShadowRoot = getFirstFocusableElement(shadowRoot) as HTMLElement;
  !isNullOrUndefined(firstFocusableInShadowRoot) ? firstFocusableInShadowRoot.focus() : null;
};

/**
 * Sets focus on the given element. If the element has a shadowroot it will set focus to the first focusable element in the shadowroot
 * @param {HTMLElement} element Element
 */
const focusElement = (element: HTMLElement): void => {
  if (!isNullOrUndefined(element.shadowRoot)) {
    focusElementInShadowRoot(element.shadowRoot);
  } else {
    element.focus();
  }
};

/**
 * Observer that observes the given element and fires a callback when it becoes visible
 * If IntersectionObserver is not supported by the browser it will use a timeout
 * @param {Element} element to observe
 * @param {Function} onIntersectionCallback Callback when element becomes visible
 */
export const observeContainer = (element: Element, onIntersectionCallback: (observedElement: Element) => void): void => {
  if ('IntersectionObserver' in window) {
    const intersectionObserver = new IntersectionObserver((entries) => {
      entries.map((entry) => {
        if (entry.isIntersecting) {
          onIntersectionCallback(entry.target);
          intersectionObserver.unobserve(element);
        }
      });
    });
    intersectionObserver.observe(element);
  } else {
    setTimeout(() => {
      onIntersectionCallback(element);
    }, 50);
  }
};

/**
 * Given a container element, it will set focus on the first tabbable element and fire callback when tabbing out of the container. Respects shift+tab
 * @param {Element} containerElement The container element for focus
 * @param {Function} onFocusOutCallback Callback when "focusout" triggers when leaving the container
 * @param {boolean} startFromEnd Optional: Passing true will focus the last element
 */
const focusArea = (containerElement: Element, onFocusOutCallback?: (event: FocusEvent) => void, startFromEnd = false): void => {
  // get all tabbable elements in the container
  const focusable = getFocusableElements(containerElement) as HTMLElement[];

  // add the container to the array if it has tabIndex
  if (parseInt(containerElement.getAttribute('tabIndex'), 10) >= 0) {
    focusable.unshift(containerElement as HTMLElement);
  }
  // get last tabbable element in container
  const lastFocusable = focusable[focusable.length - 1];
  // get first tabbable element in container
  const firstFocusable = focusable[0];

  if (focusable.length > 0) {
    if (startFromEnd) {
      focusElement(lastFocusable);
    } else {
      focusElement(firstFocusable);
    }

    // we need a function reference to be used for the even listener so we can remove it when it's over.
    const onEdgeElementBlur = (evt: FocusEvent): void => {
      const nextElement = evt.relatedTarget as Element;
      //check if the next element is within the container still
      if (isNullOrUndefined(nextElement) || getIsElementDescendantOfElement(nextElement, containerElement)) {
        return;
      }
      lastFocusable.removeEventListener('blur', onEdgeElementBlur);
      firstFocusable.removeEventListener('blur', onEdgeElementBlur);
      if (!isNullOrUndefined(onFocusOutCallback)) {
        onFocusOutCallback(evt);
      }
    };

    lastFocusable.addEventListener('blur', onEdgeElementBlur);
    firstFocusable.addEventListener('blur', onEdgeElementBlur);
  }
};

/**
 * Sets focus on the first tabbable element in a container when it becomes visible. Executes the callback function when the user is tabbing out of the container. Respects shift+tab
 * @param {Element} areaContainer Container to focus on
 * @param {Function} onFocusOutCallback Fires when "focusout" triggers on the last tabbable element
 * @param {boolean} startFromEnd Optional: Passing true will focus the last element
 */
export const setFocusOnArea = (areaContainer: Element, onFocusOutCallback?: (event: FocusEvent) => void, startFromEnd = false): void => {
  observeContainer(areaContainer, (observedElement: Element) => {
    focusArea(observedElement, onFocusOutCallback, startFromEnd);
  });
};
